import React, { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { shallowEqual } from 'fast-equals';

/**
 * Use this hook to handle clicks inside of a card which contains a link.
 * Add this to the root element, when pressed inside the card, the link will be clicked.
 * If the click hits a button or anchor, the event will be ingored.
 */ function useClickLinkInside(link) {
    const handleClick = useCallback((e)=>{
        const target = e.target;
        if (target.closest("a") || target.closest("button")) {
            return;
        }
        if (link.current) {
            link.current.click();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return handleClick;
}

const useClickOutside = (ref, handler)=>{
    useEffect(()=>{
        const handleClickOutside = (event)=>{
            if (ref.current && !ref.current.contains(event.target)) {
                handler(event);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return ()=>{
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [
        ref,
        handler
    ]);
};

function useControllableState(value, controlledValue, onChange) {
    const [internalValue, setInternalValue] = useState(value);
    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;
    const setValue = (value)=>{
        onChange?.(value);
        setInternalValue(value);
    };
    return [
        currentValue,
        setValue
    ];
}

/**
 * Creates a debounced version of the provided callback function
 */ const useDebouncedCallback = (func, wait)=>{
    const timeout = useRef(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback((...args)=>{
        const callback = ()=>{
            clearTimeout(timeout.current);
            func(...args);
        };
        clearTimeout(timeout.current);
        timeout.current = setTimeout(callback, wait);
    }, [
        func,
        wait
    ]);
};
/**
 * Returns a debounced version of the provided value
 */ function useDebouncedValue(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(()=>{
        const resolvedDelay = typeof delay === "function" ? delay(value) : delay;
        const timer = setTimeout(()=>setDebouncedValue(value), resolvedDelay);
        return ()=>{
            clearTimeout(timer);
        };
    }, [
        value,
        delay
    ]);
    return debouncedValue;
}

/**
 * Suppress the warning when using useLayoutEffect with SSR. (https://reactjs.org/link/uselayouteffect-ssr)
 * Make use of useInsertionEffect if available.
 */ const useInsertionEffect = typeof window !== "undefined" ? React.useInsertionEffect || React.useLayoutEffect : ()=>{};
/**
 * Similar to useCallback, with a few subtle differences:
 * - The returned function is a stable reference, and will always be the same between renders
 * - No dependency lists required
 * - Properties or state accessed within the callback will always be "current"
 */ function unstable_useEffectEvent(callback) {
    // Keep track of the latest callback:
    const latestRef = React.useRef(useEvent_shouldNotBeInvokedBeforeMount);
    useInsertionEffect(()=>{
        latestRef.current = callback;
    }, [
        callback
    ]);
    // Create a stable callback that always calls the latest callback:
    // using useRef instead of useCallback avoids creating and empty array on every render
    const stableRef = React.useRef(null);
    if (!stableRef.current) {
        stableRef.current = function() {
            return latestRef.current.apply(this, arguments);
        };
    }
    return stableRef.current;
}
/**
 * Render methods should be pure, especially when concurrency is used,
 * so we will throw this error if the callback is called while rendering.
 */ function useEvent_shouldNotBeInvokedBeforeMount() {
    throw new Error("INVALID_USEEVENT_INVOCATION: the callback from useEvent cannot be invoked before the component has mounted.");
}

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const useHydrated = ()=>{
    const [hydrated, setHydrated] = useState(false);
    useIsomorphicLayoutEffect(()=>{
        setHydrated(true);
    }, []);
    return hydrated;
};

function useIntersectionObserver(options) {
    const [entry, setEntry] = useState(null);
    const ref = useRef(null);
    const observer = useRef(null);
    useLayoutEffect(()=>{
        observer.current = new IntersectionObserver(([entry])=>{
            if (entry) {
                setEntry(entry);
            }
        }, options);
        const { current: currentObserver } = observer;
        currentObserver.observe(ref.current);
        return ()=>currentObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options?.root,
        options?.rootMargin,
        options?.threshold
    ]);
    return [
        ref,
        entry,
        observer.current
    ];
}
function useIntersectionObserverAPI(callback, options) {
    const observer = useRef(typeof window !== "undefined" ? new IntersectionObserver(callback, options) : null);
    useLayoutEffect(()=>{
        const { current: currentObserver } = observer;
        return ()=>currentObserver?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options?.root,
        options?.rootMargin,
        options?.threshold
    ]);
    return observer.current;
}

const IS_SERVER = typeof window === "undefined";
function useMediaQuery(query, { defaultValue = false, initializeWithValue = false } = {}) {
    const getMatches = (query)=>{
        if (IS_SERVER) {
            return defaultValue;
        }
        return window.matchMedia(query).matches;
    };
    const [matches, setMatches] = useState(()=>{
        if (initializeWithValue) {
            return getMatches(query);
        }
        return defaultValue;
    });
    // Handles the change event of the media query.
    function handleChange() {
        setMatches(getMatches(query));
    }
    useIsomorphicLayoutEffect(()=>{
        const matchMedia = window.matchMedia(query);
        // Triggered at the first client-side load and if query changes
        handleChange();
        // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
        if (matchMedia.addListener) {
            matchMedia.addListener(handleChange);
        } else {
            matchMedia.addEventListener("change", handleChange);
        }
        return ()=>{
            // Safari
            if (matchMedia.removeListener) {
                matchMedia.removeListener(handleChange);
            } else {
                matchMedia.removeEventListener("change", handleChange);
            }
        };
    }, [
        query
    ]);
    return matches;
}

const useMount = (callback)=>{
    useEffect(()=>{
        return callback();
    }, []);
};
const useLayoutMount = (callback)=>{
    useLayoutEffect(()=>{
        return callback();
    }, []);
};

const defaultMouseState = {
    x: 0,
    y: 0
};
function useMouseCallback(callback) {
    const ref = React.useRef(null);
    useLayoutEffect(()=>{
        const handleMouseMove = (event)=>{
            let newState = {
                x: event.pageX,
                y: event.pageY
            };
            if (ref.current?.nodeType === Node.ELEMENT_NODE) {
                const { left, top } = ref.current.getBoundingClientRect();
                const elementPositionX = left + window.scrollX;
                const elementPositionY = top + window.scrollY;
                const elementX = event.pageX - elementPositionX;
                const elementY = event.pageY - elementPositionY;
                newState.elementX = elementX;
                newState.elementY = elementY;
                newState.elementPositionX = elementPositionX;
                newState.elementPositionY = elementPositionY;
            }
            callback(newState);
        };
        document.addEventListener("mousemove", handleMouseMove);
        return ()=>{
            document.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);
    return [
        ref
    ];
}

function useOrientation() {
    const [orientation, setOrientation] = useState({
        beta: 0,
        alpha: 0,
        gamma: 0
    });
    useLayoutEffect(()=>{
        const handle_orientationchange = (e)=>{
            setOrientation({
                beta: e?.beta || 0,
                alpha: e?.alpha || 0,
                gamma: e?.gamma || 0
            });
        };
        window.addEventListener("deviceorientation", handle_orientationchange);
        return ()=>{
            window.removeEventListener("deviceorientation", handle_orientationchange);
        };
    }, []);
    return orientation;
}
const defaultOrientation = {
    beta: 0,
    alpha: 0,
    gamma: 0
};
function useOrientationCallback(callback) {
    useLayoutEffect(()=>{
        const handle_orientationchange = (e)=>{
            callback({
                beta: e?.beta || 0,
                alpha: e?.alpha || 0,
                gamma: e?.gamma || 0
            });
        };
        window.addEventListener("deviceorientation", handle_orientationchange);
        return ()=>{
            window.removeEventListener("deviceorientation", handle_orientationchange);
        };
    }, []);
}

/**
 * Calls the callback function when the window is resized
 * @param callback - Must be a stable reference function
 */ const useWindowResize = (callback)=>{
    useLayoutEffect(()=>{
        window.addEventListener("resize", callback);
        return ()=>{
            window.removeEventListener("resize", callback);
        };
    }, []);
};

const useResizeObserver = ()=>{
    const [entry, setEntry] = useState(null);
    const [ref] = useResizeObserverCallback((entry)=>{
        setEntry(entry);
    });
    return [
        ref,
        entry
    ];
};
const useResizeObserverCallback = (callback)=>{
    const ref = useRef(null);
    useLayoutEffect(()=>{
        const { current } = ref;
        const observer = new ResizeObserver((entries)=>{
            const entry = entries[0];
            if (entry) {
                callback(entry);
            }
        });
        if (current) {
            observer.observe(current);
        }
        return ()=>{
            observer.disconnect();
        };
    }, []);
    return [
        ref
    ];
};

// eslint-disable-next-line no-unused-vars
const useScroll = (callback)=>{
    useEffect(()=>{
        const handleScroll = ()=>{
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;
            callback({
                y: scrollY,
                x: scrollX,
                isTop: scrollY <= 0,
                isBottom: scrollY >= 0
            });
        };
        window.addEventListener("scroll", handleScroll);
        return ()=>window.removeEventListener("scroll", handleScroll);
    }, []);
};

/**
 * @param lock - if true, prevents all scrolling by hijacking the scroll events
 */ const useScrollLock = (lock = false)=>{
    useEffect(()=>{
        if (!lock) {
            document.body.classList.remove("scroll-locked");
            return;
        }
        document.body.classList.add("scroll-locked");
        return ()=>{
            document.body.classList.remove("scroll-locked");
        };
    }, [
        lock
    ]);
};

function useShallow(obj) {
    const ref = useRef(obj);
    // Update ref only if the object has changed shallowly
    useEffect(()=>{
        if (!ref.current || !shallowEqual(ref.current, obj)) {
            ref.current = obj;
        }
    }, [
        obj
    ]);
    return ref.current;
}

/**
 * Returns a ref, and a stateful value bound to the ref
 */ function useSticky() {
    const stickyRef = useRef(null);
    const [sticky, setSticky] = useState(false);
    useEffect(()=>{
        // Observe when ref enters or leaves sticky state
        // rAF https://stackoverflow.com/questions/41740082/scroll-events-requestanimationframe-vs-requestidlecallback-vs-passive-event-lis
        function observe() {
            if (!stickyRef.current) return;
            const refPageOffset = stickyRef.current.getBoundingClientRect().top;
            const stickyOffset = parseInt(getComputedStyle(stickyRef.current).top);
            const stickyActive = refPageOffset <= stickyOffset;
            if (stickyActive && !sticky) setSticky(true);
            else if (!stickyActive && sticky) setSticky(false);
        }
        observe();
        // Bind events
        document.addEventListener("scroll", observe);
        window.addEventListener("resize", observe);
        window.addEventListener("orientationchange", observe);
        return ()=>{
            document.removeEventListener("scroll", observe);
            window.removeEventListener("resize", observe);
            window.removeEventListener("orientationchange", observe);
        };
    }, [
        sticky
    ]);
    return [
        stickyRef,
        sticky
    ];
}

export { defaultMouseState, defaultOrientation, unstable_useEffectEvent, useClickLinkInside, useClickOutside, useControllableState, useDebouncedCallback, useDebouncedValue, useHydrated, useIntersectionObserver, useIntersectionObserverAPI, useIsomorphicLayoutEffect, useLayoutMount, useMediaQuery, useMount, useMouseCallback, useOrientation, useOrientationCallback, useResizeObserver, useResizeObserverCallback, useScroll, useScrollLock, useShallow, useSticky, useWindowResize };
